(() => {
  if (customElements.get('hamamat-predictive-search')) return;

  class HamamatPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="q"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-search-panel]');
      this.list = this.querySelector('[data-search-list]');
      this.message = this.querySelector('[data-search-message]');
      this.status = this.querySelector('[data-search-status]');
      this.all = this.querySelector('[data-search-all]');
      if (!this.input || !this.form || !this.panel || !this.list || !this.all) return;

      this.cache = new Map();
      this.sequence = 0;
      this.activeIndex = -1;
      this.pointerInPanel = false;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input.addEventListener('input', (event) => {
        if (!event.isComposing) this.queue();
      }, options);
      this.input.addEventListener('compositionend', () => this.queue(), options);
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim()) this.queue();
      }, options);
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.form.addEventListener('submit', () => this.close(), options);
      this.all.addEventListener('click', (event) => this.onViewAll(event), {
        ...options,
        capture: true,
      });
      // Safari can leave focus on the body when a link is pressed. Keep the
      // dropdown mounted through pointerdown -> blur -> pointerup -> click.
      this.panel.addEventListener('pointerdown', () => {
        this.pointerInPanel = true;
      }, options);
      const releasePointer = (event) => {
        if (!this.pointerInPanel) return;
        const releasedInside = this.panel.contains(event.target);
        clearTimeout(this.pointerReleaseTimer);
        this.pointerReleaseTimer = setTimeout(() => {
          this.pointerInPanel = false;
          if (!releasedInside && !this.contains(document.activeElement)) this.close();
        }, 0);
      };
      document.addEventListener('pointerup', releasePointer, options);
      document.addEventListener('pointercancel', releasePointer, options);
      this.querySelector('[data-search-close]').addEventListener('click', () => {
        this.input.focus();
        this.close();
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.close();
      }, options);
      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (!this.pointerInPanel && !this.contains(document.activeElement)) this.close();
        });
      }, options);
      this.addEventListener('pointerover', (event) => {
        if (event.target.closest('[data-product-option]')) this.setActive(-1);
      }, options);
      window.addEventListener('resize', () => this.close(), options);
    }

    disconnectedCallback() {
      this.cancel();
      clearTimeout(this.pointerReleaseTimer);
      this.events?.abort();
    }

    cancel() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.sequence += 1;
      this.list?.removeAttribute('aria-busy');
    }

    close() {
      this.cancel();
      clearTimeout(this.pointerReleaseTimer);
      this.pointerInPanel = false;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.setActive(-1);
      this.status.textContent = '';
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.positionPanel();
    }

    positionPanel() {
      // Reset before measuring so repeated openings never accumulate an offset.
      this.panel.style.setProperty('--hamamat-panel-shift', '0px');
      const bounds = this.panel.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const margin = 12;
      let shift = 0;
      if (bounds.left < margin) {
        shift = margin - bounds.left;
      } else if (bounds.right > viewportWidth - margin) {
        shift = viewportWidth - margin - bounds.right;
      }
      this.panel.style.setProperty('--hamamat-panel-shift', `${shift}px`);
    }

    setMessage(text) {
      this.message.textContent = text;
      this.message.hidden = !text;
      this.status.textContent = text;
    }

    getSearchUrl(query = this.input.value.trim()) {
      const url = new URL(this.form.action, window.location.origin);
      url.searchParams.delete('page');
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url;
    }

    onViewAll(event) {
      // Preserve Command/Ctrl-click, Shift-click, and links targeting a new tab.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (this.all.target && this.all.target !== '_self') return;
      const url = this.getSearchUrl();
      this.all.href = url.href;
      event.preventDefault();
      event.stopPropagation();
      this.close();
      window.location.assign(url.href);
    }

    queue() {
      this.cancel();
      this.setActive(-1);
      this.list.replaceChildren();
      const query = this.input.value.trim();
      if (!query) {
        this.close();
        return;
      }
      this.all.href = this.getSearchUrl(query).href;
      this.all.textContent = `View all results for “${query}”`;
      this.setMessage(this.dataset.loadingText);
      this.open();
      const sequence = this.sequence;
      // Invalidate old requests immediately; debounce only the next fetch.
      this.timer = setTimeout(() => this.search(query, sequence), 250);
    }

    async search(query, sequence) {
      if (sequence !== this.sequence) return;
      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }
      this.request = new AbortController();
      this.list.setAttribute('aria-busy', 'true');
      try {
        const endpoint = new URL(this.dataset.endpoint, window.location.origin);
        endpoint.searchParams.set('q', query);
        endpoint.searchParams.set('resources[type]', 'product');
        endpoint.searchParams.set('resources[limit]', '6');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
        endpoint.searchParams.set('section_id', 'hamamat-predictive-search');
        const response = await fetch(endpoint.href, {
          signal: this.request.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
        const html = await response.text();
        if (sequence !== this.sequence || this.input.value.trim() !== query) return;
        const results = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('[data-hamamat-search-results]');
        if (!results) throw new Error('Predictive search section is missing');
        // A small per-field cache; Theme Editor replacements get fresh instances.
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, results);
        this.render(results);
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        this.list.replaceChildren();
        this.setMessage(this.dataset.errorText);
      } finally {
        if (sequence === this.sequence) this.list.removeAttribute('aria-busy');
      }
    }

    render(results) {
      const source = results.querySelector('[data-result-items]');
      this.list.replaceChildren(...Array.from(source.children, (item) => item.cloneNode(true)));
      this.options.forEach((option, index) => {
        option.id = `${this.input.id}-option-${index}`;
      });
      this.setActive(-1);
      const count = this.options.length;
      this.setMessage(count ? '' : results.querySelector('[data-empty-text]').textContent);
      if (count) this.status.textContent = `${count} product suggestions. Use the up and down arrow keys to choose.`;
      this.open();
    }

    get options() {
      return Array.from(this.list.querySelectorAll('[data-product-option]'));
    }

    setActive(index) {
      this.activeIndex = index;
      const options = this.options;
      options.forEach((option, position) => {
        option.setAttribute('aria-selected', String(position === index));
      });
      if (index >= 0 && options[index]) {
        this.input.setAttribute('aria-activedescendant', options[index].id);
        options[index].scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKeydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Enter' && !this.panel.hidden && this.activeIndex >= 0) {
        const selected = this.options[this.activeIndex];
        if (selected) {
          event.preventDefault();
          selected.click();
        }
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      if (this.panel.hidden) {
        if (this.input.value.trim()) {
          event.preventDefault();
          this.queue();
        }
        return;
      }
      const count = this.options.length;
      if (!count) return;
      event.preventDefault();
      const next = this.activeIndex < 0
        ? (event.key === 'ArrowDown' ? 0 : count - 1)
        : (this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + count) % count;
      this.setActive(next);
    }
  }

  customElements.define('hamamat-predictive-search', HamamatPredictiveSearch);
})();
